import { useEffect, useRef } from "react";

/**
 * Traps browser Back/Forward so the user stays on the current URL.
 * Cross-browser (Chrome, Firefox, Edge, Safari):
 * - pushState trap on native popstate
 * - react-router history.block for POP when history is provided
 *
 * Call releaseLock() before intentional navigation (e.g. Start Over).
 */
export const useLockBrowserHistory = (enabled = true, history = null) => {
  const unlockedRef = useRef(false);
  const unblockRef = useRef(null);

  const releaseLock = () => {
    unlockedRef.current = true;
    if (typeof unblockRef.current === "function") {
      unblockRef.current();
      unblockRef.current = null;
    }
  };

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return undefined;
    }

    unlockedRef.current = false;

    const lockUrl =
      window.location.pathname +
      window.location.search +
      window.location.hash;

    const pushLockState = () => {
      if (unlockedRef.current) {
        return;
      }
      try {
        window.history.pushState({ __browserNavLock: 1 }, "", lockUrl);
      } catch {
        // Safari may throw in rare restricted history states.
      }
    };

    // Seed multiple entries so Back and Forward stay in the trap.
    pushLockState();
    pushLockState();

    const handlePopState = () => {
      if (unlockedRef.current) {
        return;
      }
      pushLockState();
    };

    window.addEventListener("popstate", handlePopState);

    if (history && typeof history.block === "function") {
      unblockRef.current = history.block((_location, action) => {
        if (unlockedRef.current) {
          return undefined;
        }
        if (action === "POP") {
          pushLockState();
          return false;
        }
        return undefined;
      });
    }

    return () => {
      window.removeEventListener("popstate", handlePopState);
      if (typeof unblockRef.current === "function") {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [enabled, history]);

  return { releaseLock };
};
